import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { SortableTable } from '../ui/SortableTable';
import { FreshnessCell } from './FreshnessCell';
import type { ColumnDef } from '../ui/SortableTable';
import type { DossierProvinceRow } from '../../types/dossier';
import type { CrystalBallData, SurveyData } from '../../types/intel';
import { formatNumber, formatEnumLabel } from '../../utils/format';

interface ResourcesTabProps {
  provinces: DossierProvinceRow[];
  currentTick: number;
  sortState: { key: string; dir: 'asc' | 'desc' | 'none' };
  onSort: (key: string) => void;
}

function computeBuiltPercent(cb: CrystalBallData | null, survey: SurveyData | null): string {
  if (!cb || !survey) return '—';
  const totalBuilt = Object.values(survey.buildings).reduce((sum, count) => {
    return sum + (count ?? 0);
  }, 0);
  if (cb.totalLand <= 0) return '—';
  return `${((totalBuilt / cb.totalLand) * 100).toFixed(1)}%`;
}

export function ResourcesTab({ provinces, currentTick, sortState, onSort }: ResourcesTabProps) {
  const navigate = useNavigate();

  const columns: ColumnDef<DossierProvinceRow>[] = [
    {
      key: 'provinceName',
      header: 'Province',
      sticky: true,
      sortable: true,
      sortAccessor: (r) => r.provinceName,
      render: (r) => (
        <span>
          <span style={{ color: COLORS.text.primary, fontWeight: 500 }}>{r.provinceName}</span>
          <span style={{ color: COLORS.text.muted, marginLeft: 4, fontSize: 11 }}>({r.location})</span>
        </span>
      ),
    },
    {
      key: 'race',
      header: 'Race',
      sortable: true,
      sortAccessor: (r) => r.race,
      render: (r) => <span style={{ color: COLORS.text.tertiary }}>{formatEnumLabel(r.race)}</span>,
    },
    {
      key: 'land',
      header: 'Land',
      sortable: true,
      sortAccessor: (r) => r.land,
      render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(r.land)}</span>,
    },
    {
      key: 'builtPercent',
      header: 'Built %',
      sortable: false,
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null ?? null;
        const survey = r.latestSurvey?.data as SurveyData | null ?? null;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{computeBuiltPercent(cb, survey)}</span>;
      },
    },
    {
      key: 'gold',
      header: 'Gold',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.money ?? -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb ? (
          <span style={{ color: COLORS.gold, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(cb.money)}</span>
        ) : <span style={{ color: COLORS.text.muted }}>—</span>;
      },
    },
    {
      key: 'food',
      header: 'Food',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.food ?? -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb
          ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(cb.food)}</span>
          : <span style={{ color: COLORS.text.muted }}>—</span>;
      },
    },
    {
      key: 'runes',
      header: 'Runes',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.runes ?? -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb
          ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(cb.runes)}</span>
          : <span style={{ color: COLORS.text.muted }}>—</span>;
      },
    },
    {
      key: 'runesPerAcre',
      header: 'Runes/Acre',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb && cb.totalLand > 0 ? cb.runes / cb.totalLand : -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        if (!cb || cb.totalLand <= 0) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{(cb.runes / cb.totalLand).toFixed(1)}</span>;
      },
    },
    {
      key: 'peasants',
      header: 'Peasants',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.peasants ?? -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb
          ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(cb.peasants)}</span>
          : <span style={{ color: COLORS.text.muted }}>—</span>;
      },
    },
    {
      key: 'tradeBalance',
      header: 'Trade Bal.',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.tradeBalance ?? -999999;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        if (!cb) return <span style={{ color: COLORS.text.muted }}>—</span>;
        const color = cb.tradeBalance >= 0 ? COLORS.positive : COLORS.negative;
        return <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(cb.tradeBalance)}</span>;
      },
    },
    {
      key: 'freshness',
      header: 'Freshness',
      sortable: true,
      sortAccessor: (r) => {
        const cbTick = r.latestCb?.capturedAtTick ?? null;
        const surveyTick = r.latestSurvey?.capturedAtTick ?? null;
        const maxTick = Math.max(cbTick ?? 0, surveyTick ?? 0);
        return maxTick > 0 ? maxTick : -1;
      },
      render: (r) => {
        const cbTick = r.latestCb?.capturedAtTick ?? null;
        const surveyTick = r.latestSurvey?.capturedAtTick ?? null;
        const bestTick = cbTick !== null || surveyTick !== null
          ? Math.max(cbTick ?? 0, surveyTick ?? 0)
          : null;
        return <FreshnessCell capturedAtTick={bestTick} currentTick={currentTick} />;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => {
        const hasCb = r.latestCb !== null;
        const hasSurvey = r.latestSurvey !== null;
        if (hasCb && hasSurvey) return null;
        const params = new URLSearchParams({
          targetId: r.provinceId,
          targetName: r.provinceName,
          targetLoc: r.location,
        });
        return (
          <button
            onClick={() => navigate(`/thievery?${params.toString()}`)}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              background: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text.tertiary,
              cursor: 'pointer',
              borderRadius: 2,
            }}
          >
            Gather {!hasCb ? 'CB' : 'Survey'}
          </button>
        );
      },
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={provinces}
      getRowKey={(r) => r.provinceId}
      sortState={sortState}
      onSort={onSort}
    />
  );
}
